import { useParams, Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
              <i className="ri-error-warning-line text-foreground-400 text-3xl w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 6);

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: categoryInfo?.name || comic.category, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const articleUrl = `https://hanmanonline.com/article/${comic.id}`;
  const formattedViews = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 韩漫在线免费韩国漫画`,
    description: comic.description,
    author: {
      "@type": "Person",
      name: comic.author,
    },
    datePublished: comic.chapters[comic.chapters.length - 1]?.date,
    dateModified: comic.updateDate,
    url: articleUrl,
    image: comic.cover,
    publisher: {
      "@type": "Organization",
      name: "韩漫在线",
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <article className="pt-20">
        <Breadcrumb items={breadcrumbItems} />
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0">
                <div className="w-[240px] md:w-[280px] aspect-[5/7] rounded-lg overflow-hidden bg-background-200/70">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩漫在线韩国漫画`}
                    title={`${comic.title} - 免费韩国漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="text-sm text-foreground-500">{comic.author}</span>
                  <span className="text-foreground-300">·</span>
                  <span className="flex items-center gap-1 text-sm">
                    <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    <span className="text-foreground-700 font-medium">{comic.rating}</span>
                  </span>
                  <span className="text-foreground-300">·</span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
                    {formattedViews} 阅读
                  </span>
                  <span className="text-foreground-300">·</span>
                  <span className="px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-8">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-base font-semibold text-foreground-900">
                      章节列表
                      <span className="text-sm text-foreground-500 font-normal ml-2">
                        共 {comic.chapters.length} 话
                      </span>
                    </h2>
                    <button className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-sort-desc w-4 h-4 inline-flex items-center justify-center mr-1"></i>
                      正序
                    </button>
                  </div>
                  <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2">
                    {[...comic.chapters].reverse().map((chapter) => (
                      <div
                        key={chapter.id}
                        className="flex items-center justify-between px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-150 cursor-pointer group"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                            第{chapter.number}话
                          </span>
                          <span className="text-sm text-foreground-600">{chapter.title}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-foreground-400">{chapter.date}</span>
                          <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors w-4 h-4 flex items-center justify-center"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200/70">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  同类推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </article>
    </>
  );
}